import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Save, Coins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import AdminLayout from "@/components/layout/AdminLayout";
import { useToast } from "@/hooks/use-toast";
import { fetchCreditTiers, updateCreditTier } from "@/api/ai-credits";
import type { AiCreditTier } from "@/types/aiCredits";

export default function AiCreditConfig() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<{
    credits_per_period: number;
    reset_period_hours: number;
  }>({ credits_per_period: 0, reset_period_hours: 5 });

  const { data: tiers = [], isLoading } = useQuery({
    queryKey: ["ai-credit-tiers"],
    queryFn: fetchCreditTiers,
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: number;
      updates: Partial<Pick<AiCreditTier, "credits_per_period" | "reset_period_hours" | "is_active">>;
    }) => updateCreditTier(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-credit-tiers"] });
      setEditingId(null);
      toast({ title: "Tier updated successfully" });
    },
    onError: (err) => {
      toast({
        title: "Failed to update tier",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    },
  });

  const startEditing = (tier: AiCreditTier) => {
    setEditingId(tier.id);
    setEditValues({
      credits_per_period: tier.credits_per_period,
      reset_period_hours: tier.reset_period_hours,
    });
  };

  const saveEditing = () => {
    if (editingId === null) return;
    updateMutation.mutate({ id: editingId, updates: editValues });
  };

  const toggleActive = (tier: AiCreditTier) => {
    updateMutation.mutate({
      id: tier.id,
      updates: { is_active: !tier.is_active },
    });
  };

  const formatCredits = (n: number) => {
    if (n >= 999999999) return "Unlimited";
    return n.toLocaleString();
  };

  return (
    <AdminLayout title="AI Credit Configuration">
      <div className="space-y-6 p-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Coins className="w-5 h-5" />
              Credit Tiers
            </CardTitle>
            <CardDescription>
              Configure how many AI credits each user tier receives per rolling
              time window. Credits = tokens (prompt + completion) consumed per
              AI interaction.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="text-sm text-muted-foreground">Loading...</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tier</TableHead>
                    <TableHead>Credits / Period</TableHead>
                    <TableHead>Reset Period (hours)</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tiers.map((tier) => (
                    <TableRow key={tier.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">
                            {tier.display_name}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {tier.tier_key}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        {editingId === tier.id ? (
                          <Input
                            type="number"
                            value={editValues.credits_per_period}
                            onChange={(e) =>
                              setEditValues((v) => ({
                                ...v,
                                credits_per_period: parseInt(e.target.value) || 0,
                              }))
                            }
                            className="w-40"
                          />
                        ) : (
                          <span className="font-mono">
                            {formatCredits(tier.credits_per_period)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {editingId === tier.id ? (
                          <Input
                            type="number"
                            value={editValues.reset_period_hours}
                            onChange={(e) =>
                              setEditValues((v) => ({
                                ...v,
                                reset_period_hours: parseInt(e.target.value) || 1,
                              }))
                            }
                            className="w-24"
                            min={1}
                          />
                        ) : (
                          <span>{tier.reset_period_hours}h</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={tier.is_active}
                          onCheckedChange={() => toggleActive(tier)}
                          disabled={updateMutation.isPending}
                        />
                      </TableCell>
                      <TableCell>
                        {editingId === tier.id ? (
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              onClick={saveEditing}
                              disabled={updateMutation.isPending}
                            >
                              <Save className="w-3 h-3 mr-1" />
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditingId(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => startEditing(tier)}
                          >
                            Edit
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">How it works</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-2">
            <p>
              <strong>Credits = tokens.</strong> Each AI interaction consumes
              prompt tokens + completion tokens. Direct answer bypasses (metadata
              questions) cost a flat 50 credits.
            </p>
            <p>
              <strong>Rolling window.</strong> Credits reset on a rolling basis.
              If the reset period is 5 hours, the system counts tokens used in
              the last 5 hours.
            </p>
            <p>
              <strong>Tier detection:</strong> Admin (roles 1-4) &gt; Subscriber
              (active subscription) &gt; Free (everyone else).
            </p>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
